import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { Subject } from 'rxjs';

interface ISectionEditDialogData {
    sectionNames: IContentLanguageLabelModel[];
    selectedLanguageId: number;
    isSectionNameRequired: boolean;
}

@Component({
    selector: 'fi-section-edit-dialog',
    template: `
        <p class="message-text">{{ 'common.edit_later_info' | fiAppText }}</p>
        <fi-name-input
            [(ngModel)]="data.sectionNames"
            [labelTextId]="'checklist.section_name' | fiAppText"
            [isRequired]="data.isSectionNameRequired"
            [hintTextId]="'checklist.section_name_hint' | fiAppText"
            [requiredErrorTextId]="'checklist.section_name_hint' | fiAppText"
            [selectedLanguageId]="data.selectedLanguageId"
        />
    `,
    styleUrls: ['./fi-section-edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-section-edit-dialog' },
    standalone: false,
})
export class FiSectionEditDialogComponent
    extends FiDialogsContentBase<ISectionEditDialogData, IContentLanguageLabelModel[]>
    implements OnInit, OnDestroy
{
    private fiDialogsService: FiDialogsService = inject(FiDialogsService);
    private fiValuesComparison: FiValuesComparison<IContentLanguageLabelModel[]>;
    private readonly maxlength = 200;

    public ngOnInit(): void {
        this.fiValuesComparison = new FiValuesComparison(() => this.data.sectionNames);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    public getResult(): IContentLanguageLabelModel[] {
        return this.data.sectionNames;
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    public isYesButtonDisabled(): boolean {
        const sectionName = this.data.sectionNames.find((name: IContentLanguageLabelModel) => {
            return name.ContentLanguageId === this.data.selectedLanguageId;
        });
        return (
            (this.data.isSectionNameRequired && sectionName?.Value === '') ||
            sectionName?.Value?.length > this.maxlength
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
